import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Add } from "@mui/icons-material";
import { Box, Button } from '@mui/material';
import { Grid } from "@mui/material";
import { DataGrid, GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { useMutation, useQuery } from "@tanstack/react-query";
import CatalogAPI from "api/faber/CatalogAPI";
import NormalText from "common/Typography/NormalText";
import OrbitSession from "hooks/useCookies";

export interface ManageAddPageCatalogProps {
    pageId: string
    handleClose: () => void;
}

export default function AddCatalogToPage({
    pageId,
    handleClose
}: ManageAddPageCatalogProps) {
    const faberToken = OrbitSession.get("faberToken");
    const [catalogRows, setCatalogRows] = useState<GridRowDataType[]>([]);

    interface GridRowDataType {
        id: string;
        description?: string[];
        name?: string;
        dataInGB?: number;
        packageType?: string;
        tenant?: string;
        validityInDays?: number;
        currency?: string;
        value?: string;
        price?: {
            currency: string;
            value: string;
        };
        provisionMethod?: {
            method: string;
        };
    }

    const { mutate: addPage } = useMutation(CatalogAPI.AddFkCatalog, {
        onSuccess: () => {
            toast.success('Catalog added successfully!');
            handleClose();
        },
        onError: (error) => {
            console.error("Failed to add catalog:", error);
            toast.error('Failed to add Catalog!');
        }
    });

    const handleAddCatalog = (values: PageFkCatalog) => {
        const allValues = {
            catalogPageID: values.catalogPageID,
            catalogID: values.catalogID,
            faberToken: faberToken
        };
        const allValuesArray = [allValues];
        addPage(allValuesArray);
    };

    const columns: GridColDef[] = [
        {
            field: "name",
            headerName: "Name",
            width: 250,
            filterable: true,
        },
        { field: "dataInGB", headerName: "Data In GB", width: 100 },
        {
            field: "packageType",
            headerName: "Package Type",
            width: 150,
        },
        { field: "tenant", headerName: "Tenant", width: 100 },
        { field: "validityInDays", headerName: "Validity In Days", width: 100 },
        {
            field: "price.currency",
            headerName: "Currency",
            width: 100,
            valueGetter: (params) => params.row?.price?.currency,
        },
        {
            field: "price.value",
            headerName: "Value",
            width: 100,
            valueGetter: (params) => params.row?.price?.value,
        },
        {
            field: "provisionMethod.method",
            headerName: "Provision Method",
            width: 150,
            valueGetter: (params) => params.row?.provisionMethod?.method,
        },
        {
            field: "addCatalog",
            flex: 0.75,
            width: 200,
            filterable: false,
            sortable: false,
            headerName: "Add Catalog",
            renderCell: (params: GridRenderCellParams<GridRowDataType>) => {
                const { row } = params;
                return (
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => handleAddCatalog({ catalogPageID: pageId, catalogID: row.id, faberToken: faberToken! })}
                    >
                        <Add />
                    </Button>
                );
            },
        },
    ];

    const { data: catalogItems, isLoading: isLoadingAllCatalogs } = useQuery(
        ["getCatalogItems"],
        async () => CatalogAPI.getAllCatalogItems(faberToken)
    );

    const { data: existingCatalogItems, isLoading: isLoadingExistingCatalogs } = useQuery(
        [`getAllCatalogItemsById_${pageId}`],
        async () => CatalogAPI.getAllCatalogItemsById(faberToken, pageId)
    );

    useEffect(() => {
        const allCatalogs: GridRowDataType[] = Array.isArray(catalogItems) ? catalogItems : [];
        const currentExistingCatalogs: GridRowDataType[] = Array.isArray(existingCatalogItems)
            ? existingCatalogItems
            : [];

        if (allCatalogs.length > 0) {
            const existingCatalogIds = new Set(currentExistingCatalogs.map(catalog => catalog.id));

            const filteredCatalogs = allCatalogs.filter((catalog: GridRowDataType) => {
                return !existingCatalogIds.has(catalog.id);
            });

            setCatalogRows(filteredCatalogs);
        } else {
            setCatalogRows([]);
        }

    }, [catalogItems, existingCatalogItems]);

    useEffect(() => {
        const allCatalogs: GridRowDataType[] = Array.isArray(catalogItems) ? catalogItems : [];
        const currentExistingCatalogs: GridRowDataType[] = Array.isArray(existingCatalogItems)
            ? existingCatalogItems
            : [];

        if (allCatalogs.length > 0) {
            const existingCatalogIds = new Set(currentExistingCatalogs.map(catalog => catalog.id));
            const filteredCatalogs = allCatalogs.filter((catalog: GridRowDataType) => {
                return !existingCatalogIds.has(catalog.id);
            });
            setCatalogRows(filteredCatalogs);
        } else {
            setCatalogRows([]);
        }

    }, [catalogItems, existingCatalogItems]);

    return (
        <Box sx={{ minWidth: "1000px" }}>
            <NormalText isBold>Catalog Items</NormalText>
            <Grid item xs={12} height={640}>
                <DataGrid
                    columns={columns}
                    rows={catalogRows}
                    getRowId={(row) => row.id}
                    onRowClick={(row) => console.log(row)}
                    rowsPerPageOptions={[10]}
                    pageSize={10}
                    loading={isLoadingAllCatalogs || isLoadingExistingCatalogs}
                />
            </Grid>
        </Box>
    );
}

interface PageFkCatalog {
    catalogPageID: string;
    catalogID: string;
    faberToken: string;
}